// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useMemo } from 'react';
import { Stack, useLocalSearchParams } from 'expo-router';
import { ScrollView, StyleSheet, Text } from 'react-native';

import { useUser } from '@/app/user-context';
import { CourseAnalysisStatusPanel } from '@/features/course-trace/CourseAnalysisStatusPanel';
import { HoleChecksSection } from '@/features/course-trace/HoleChecksSection';
import { useEffectiveRoles } from '@/hooks/useEffectiveRoles';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import appColors from '@/theme/colors';

/**
 * One course's analysis diagnostics on a screen of their own.
 *
 * The panel used to open in place inside the course list's detail popup, which put a run
 * history, a job list and two columns of external links inside a card the size of a phone.
 * Reached by a button now, it gets the whole screen — and the popup keeps its buttons.
 */
export default function CourseAnalysisStatusScreen() {
  const { t } = useI18n();
  const { courseId, name } = useLocalSearchParams<{ courseId?: string; name?: string }>();
  const { user } = useUser();
  const { isAdmin } = useEffectiveRoles(user);
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const id = typeof courseId === 'string' && courseId.length > 0 ? courseId : null;

  return (
    <>
      <Stack.Screen options={{ title: t('courseAnalysis.status.title') }} />
      <ScrollView contentContainerStyle={styles.container}>
        <Text style={styles.title}>
          {typeof name === 'string' && name.length > 0 ? name : t('courseAnalysis.status.title')}
        </Text>

        {!isAdmin ? (
          <Text style={styles.paragraph} testID="course-analysis-status-forbidden">
            {t('courseAnalysis.adminOnly')}
          </Text>
        ) : id === null ? (
          <Text style={styles.paragraph} testID="course-analysis-status-missing-course">
            {t('courseAnalysisStatusPage.noCourse')}
          </Text>
        ) : (
          <>
            <CourseAnalysisStatusPanel
              apiBaseUrl={apiBaseUrl}
              token={user?.token}
              courseId={id}
              alwaysOpen
            />
            <HoleChecksSection apiBaseUrl={apiBaseUrl} token={user?.token} courseId={id} />
          </>
        )}
      </ScrollView>
    </>
  );
}

const styles = StyleSheet.create({
  container: { padding: 16, gap: 12 },
  title: { fontSize: 26, fontWeight: '700', color: appColors.text.primary },
  paragraph: { color: appColors.text.secondary },
});
